import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import dayjs from 'dayjs';
import { getTrackerList } from '../../../../../redux/features/tutorReplacementTrackerSlice';
import { ASSIGNMENT_FILTERS, PROGRESS_FILTERS, TUTOR_STATUS_FILTERS, VIEWS } from '../utils/trackerConstants';

const ALL = 'all';
const toSelected = (item) => ({ id: item.id, value: item.name });

const INITIAL_TABLE_STATE = {
  page: 1,
  pageSize: 10,
  search: '',
  sort: { field: '', direction: 'asc' },
  frozenColumns: [],
  hiddenColumns: [],
};

// Filters, table state and rows for one tracker page (`view` = head | mine | cro). `reload` refetches after a save.
export default function useTrackerList(view, { enabled = true } = {}) {
  const dispatch = useDispatch();
  const [list, setList] = useState({ loading: true, data: [], meta: {} });
  const [refreshKey, setRefreshKey] = useState(0);
  const [fromDate, setFromDate] = useState(dayjs().startOf('month').format('YYYY-MM-DD'));
  const [toDate, setToDate] = useState(dayjs().endOf('month').format('YYYY-MM-DD'));
  const [tutorStatus, setTutorStatus] = useState(toSelected(TUTOR_STATUS_FILTERS[0]));
  const [progress, setProgress] = useState(toSelected(PROGRESS_FILTERS[0]));
  const [assignment, setAssignment] = useState(toSelected(ASSIGNMENT_FILTERS[0]));
  const [tableState, setTableState] = useState(INITIAL_TABLE_STATE);

  // a filter change must not leave the table on a page that no longer exists
  const withReset = (setter) => (value) => {
    setter(value);
    setTableState((prev) => ({ ...prev, page: 1 }));
  };

  const dispatchParams = useMemo(
    () => ({
      view,
      page: tableState.page,
      limit: tableState.pageSize,
      search: tableState.search,
      sortBy: tableState.sort?.direction ? tableState.sort.field : '',
      sortOrder: tableState.sort?.direction ?? '',
      from: fromDate,
      to: toDate,
      tutorStatus: tutorStatus.id === ALL ? '' : tutorStatus.id,
      progress: progress.id === ALL ? '' : progress.id,
      assignment: view === VIEWS.HEAD && assignment.id !== ALL ? assignment.id : '',
    }),
    [view, tableState.page, tableState.pageSize, tableState.search, tableState.sort, fromDate, toDate, tutorStatus, progress, assignment],
  );

  useEffect(() => {
    if (!enabled) return undefined;
    setList((prev) => ({ ...prev, loading: true }));
    // a newer filter change cancels the request still in flight, so a slower older response can't overwrite it
    const promise = dispatch(getTrackerList(dispatchParams));
    promise.then((action) => {
      if (action.meta?.aborted) return;
      const ok = action.payload?.status === 'success';
      setList({ loading: false, data: ok ? action.payload.data : [], meta: ok ? action.payload.meta : {} });
    });
    return () => promise.abort();
  }, [dispatch, dispatchParams, enabled, refreshKey]);

  const reload = useCallback(() => setRefreshKey((key) => key + 1), []);
  // GlobalTable is memoised, so props that are rebuilt on every render would re-render the whole table
  const exportParams = useMemo(() => ({ ...dispatchParams, page: 1, limit: 'no' }), [dispatchParams]);

  return {
    list,
    loading: list.loading || !enabled,
    reload,
    tableState,
    setTableState,
    exportParams,
    filters: {
      fromDate,
      toDate,
      tutorStatus,
      progress,
      assignment,
      setFromDate: withReset(setFromDate),
      setToDate: withReset(setToDate),
      setTutorStatus: withReset(setTutorStatus),
      setProgress: withReset(setProgress),
      setAssignment: withReset(setAssignment),
    },
  };
}
